/**
 * Redux slice for the Entitlement tab
 *
 * This slice manages the state for entitlement tab in the AAG module.
 * It handles data for entitlement, filters, sorting, other controls, etc.
 *
 * Key features:
 * - Entitlement filters (name, application, risk level)
 * - Filter caching for performance optimization
 * - Tab navigation and active filter management
 * - Sorting and column customization
 * - Bulk actions for multiple entitlements
 * - Side panel for detailed entitlement information
 * - Modal management for mitigate, remediate, and review actions
 *
 * @file entitlementSlice.ts
 * @module Entitlement
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { ENTITLEMENT_INITIAL_FILTERS } from '../../../shared/constants';
import { detailsActions, globalActions } from '../../../shared/store/actions';
import {
  type ActiveFiltersState,
  type AdvancedFilterState,
  type InsightItemTypes,
} from '../../../shared/types';
import {
  type EntitlementColumnState,
  type EntitlementDetailsRowType,
  type EntitlementListState,
  type EntitlementSidePanelGroupRowContextState,
  type EntitlementSidePanelRowContextState,
  type EntitlementSortState,
  type EntitlementState,
  EntitlementType,
  type FilterCacheState,
} from '../data-layer/entitlementTypes';

const initialState: EntitlementState = {
  isLoading: false,
  controls: {
    filters: ENTITLEMENT_INITIAL_FILTERS,
    isInsightsOpen: true,
    activeFilters: [] as ActiveFiltersState[],
    searchKey: null,
    sortInAction: null,
    activeColumns: [] as EntitlementColumnState[],
    filterCache: {
      entries: {},
      ids: {},
      metadata: {},
    } as FilterCacheState,
    activeInsight: null,
    activeRowContext: null,
  },
  list: {
    isListLoading: false,
    totalCount: null,
    rowIdToTransition: null,
    isRefresh: false,
  } as EntitlementListState,
  bulkaction: {
    isFooterVisible: false,
    bulkCount: 0,
    isBulkChecked: false,
  },
  sidepanel: {
    isSidePanelVisible: false,
    rowContext: { current: { id: '' } },
  },
};

const entitlementSlice = createSlice({
  name: EntitlementType.SLICE_NAME,
  initialState,
  reducers: {
    // General section
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    // Controls section
    setAdvancedFilters: (
      state,
      action: PayloadAction<AdvancedFilterState[]>
    ) => {
      state.controls.filters = action.payload;
    },
    setActiveFilters: (state, action: PayloadAction<ActiveFiltersState[]>) => {
      state.controls.activeFilters = action.payload;
    },
    setFiltersCache: (state, action: PayloadAction<FilterCacheState>) => {
      state.controls.filterCache = action.payload;
    },
    setSearchKey: (state, action: PayloadAction<string | null>) => {
      state.controls.searchKey = action.payload;
    },
    setSortInAction: (
      state,
      action: PayloadAction<EntitlementSortState | null>
    ) => {
      state.controls.sortInAction = action.payload;
    },
    setViewColumns: (
      state,
      action: PayloadAction<EntitlementColumnState[]>
    ) => {
      state.controls.activeColumns = action.payload;
    },
    setIsInsightsOpen: (state, action: PayloadAction<boolean>) => {
      state.controls.isInsightsOpen = action.payload;

      if (action.payload) {
        state.sidepanel.isSidePanelVisible = false;
      }
    },
    setActiveInsight: (
      state,
      action: PayloadAction<InsightItemTypes | null>
    ) => {
      state.controls.activeInsight = action.payload;
    },
    setRowContext: (
      state,
      action: PayloadAction<EntitlementSidePanelRowContextState | null>
    ) => {
      state.controls.activeRowContext = action.payload;
    },
    // List operations
    setIsListLoading: (state, action: PayloadAction<boolean>) => {
      state.list.isListLoading = action.payload;
    },
    setListTotalCount: (state, action: PayloadAction<number | null>) => {
      state.list.totalCount = action.payload;
    },
    setListTransitionId: (
      state,
      action: PayloadAction<number | string | null>
    ) => {
      state.list.rowIdToTransition = action.payload;
    },
    setListData: (
      state,
      action: PayloadAction<EntitlementDetailsRowType[]>
    ) => {
      state.list.data = action.payload;
    },
    setDisplayRecords: (state, action: PayloadAction<number>) => {
      state.list.displayRecords = action.payload;
    },
    setListIsRefresh: (state, action: PayloadAction<boolean>) => {
      state.list.isRefresh = action.payload;
    },
    // Bulk actions section
    setBulkCount: (state, action: PayloadAction<number>) => {
      state.bulkaction.bulkCount = action.payload;
    },
    setIsBulkChecked: (state, action: PayloadAction<boolean>) => {
      state.bulkaction.isBulkChecked = action.payload;
      state.bulkaction.isFooterVisible = action.payload;
    },
    setIsFooterVisible: (state, action: PayloadAction<boolean>) => {
      state.bulkaction.isFooterVisible = action.payload;
    },
    // Sidepanel section
    setIsSidePanelVisible: (state, action: PayloadAction<boolean>) => {
      state.sidepanel.isSidePanelVisible = action.payload;

      if (action.payload) {
        state.controls.isInsightsOpen = false;
      } else {
        state.sidepanel.rowContext = { current: { id: '' } };
        // Don't auto-open insights when sidepanel closes
      }
    },
    setSidePanelRowContext: (
      state,
      action: PayloadAction<EntitlementSidePanelGroupRowContextState>
    ) => {
      state.sidepanel.rowContext = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Add extra reducers for entitlement-specific actions
    builder.addMatcher(
      (action: PayloadAction<boolean>) =>
        action.type === globalActions.setViolationsActiveTab.type ||
        action.type === detailsActions.setQueryParam.type,
      (state) => {
        state.list.totalCount = null;

        if (state.sidepanel.isSidePanelVisible) {
          state.sidepanel.isSidePanelVisible = false;
        }

        if (state.bulkaction.isFooterVisible) {
          state.bulkaction.isBulkChecked = false;
          state.bulkaction.isFooterVisible = false;
        }
      }
    );
  },
});

const { reducer: entitlementReducer, actions: sliceActions } = entitlementSlice;

export const entitlementActions = sliceActions;

export default entitlementReducer;
